import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, matchPath } from 'react-router-dom';
import { Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import isInGroup, { type Session } from '../../services/util/isInGroup';
import { ArrowIcon } from '../../theme/styled-components';
import getAccountTypeAction from '../../actions/accounttype/get';
import { MapGetter, State } from '../../utils/map';

interface AccountType {
  isTopLevel: boolean;
  isFamily: boolean;
  isDecided: boolean;
}

const RE_INDIVIDUAL_DASHBOARD_LANDING = /\/dashboard\/[a-z,-]*$/;

const useStyles = makeStyles(() => ({
  backContainer: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',

    '& > span': {
      padding: '6px 12px',
    },
  },
  backIcon: {
    display: 'flex',
    margin: '0 10px 0 0',
    height: '17px',
    transform: 'rotate(180deg)',
  },
}));

function BackButton() {
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const contactStatus = useSelector<State<string>, MapGetter<string>>((state) =>
    state.contacts.get('contactstatus'),
  );
  const accounttype = useSelector<
    State<AccountType | null>,
    MapGetter<AccountType | null>
  >((state) => state.accounttype.get('data'));
  const session = useSelector<State<Session>, MapGetter<Session>>((state) =>
    state.session.get('data'),
  );

  useEffect(() => {
    dispatch(getAccountTypeAction());
  }, [dispatch, location]);

  if (!accounttype) {
    return null;
  }

  if (isInGroup(session, 'Admin') && location.pathname === '/admin') {
    return null;
  }

  if (isInGroup(session, 'Adviser') && location.pathname === '/adviser') {
    return null;
  }

  if (!accounttype.isTopLevel) {
    if (contactStatus === 'Prospect' && location.pathname === '/actions') {
      return null;
    }

    if (location.pathname === '/dashboard') {
      return null;
    }

    if (location.pathname === '/') {
      return null;
    }

    if (
      !accounttype.isFamily &&
      location.pathname.match(RE_INDIVIDUAL_DASHBOARD_LANDING)
    ) {
      return null;
    }
  }

  const handleBack = () => {
    let redirectPath = null;

    if (isInGroup(session, 'Admin')) {
      redirectPath = '/admin';
    } else if (isInGroup(session, 'Adviser')) {
      redirectPath = '/adviser';
    }

    if (
      redirectPath &&
      (location.pathname === '/dashboard' ||
        (matchPath('/dashboard/:planownerSlug', location.pathname) &&
          !accounttype.isFamily))
    ) {
      navigate(redirectPath);
      return;
    }

    if (location.key !== 'default') {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <Button onClick={handleBack} className={classes.backContainer}>
      <ArrowIcon className={classes.backIcon} />
      <span>Back</span>
    </Button>
  );
}

export default BackButton;
